export interface TopBannerConfig {
  /** ISO datetime string (Europe/Moscow) after which the banner is hidden */
  endsAt: string;
  /** Plans that should NOT see the banner */
  hiddenForPlans: string[];
  images: Record<string, string>;
  link: Record<string, string>;
  alt: Record<string, string>;
  height: number;
}

export const YANDEX_COUNTRIES = new Set(["RU", "BY", "KZ"]);

export const TOP_BANNER: TopBannerConfig = {
  endsAt: "2025-12-06T09:00:00+03:00",
  hiddenForPlans: ["pomelo"],
  images: {
    en: `${import.meta.env.BASE_URL}img/banners/bf25_en.webp`,
    ru: `${import.meta.env.BASE_URL}img/banners/bf25_ru.webp`,
  },
  link: {
    en: "https://nextgis.com/black-friday-2025/en/?utm_source=nextgis-id&utm_medium=header&utm_campaign=black-friday25&utm_content=en",
    ru: "https://nextgis.ru/black-friday-2025/ru/?utm_source=nextgis-id&utm_medium=header&utm_campaign=black-friday25&utm_content=ru",
  },
  alt: {
    en: "All datasets and subscriptions 50% off December 1-5",
    ru: "Скидка 50% на все геоданные и подписки 1-5 декабря",
  },
  height: 80,
};

export const SUPPORTED_LANGUAGES = {
  en: "English",
  zh: "简体中文",
  es: "Español",
  fr: "Français",
  ru: "Русский",
  pt: "Português",
  de: "Deutsch",
  it: "Italiano",
  ja: "日本語",
  pl: "Polski",
  cs: "Čeština",
  bg: "Български",
  be: "Беларуская",
  he: "עברית",
  ar: "العربية",
} as const;

export type SupportedLang = keyof typeof SUPPORTED_LANGUAGES;

export const LOCALE_LABELS: Record<string, string> = SUPPORTED_LANGUAGES;

const AVAILABLE_LOCALES = Object.keys(SUPPORTED_LANGUAGES);
type AvailableLocale = (typeof AVAILABLE_LOCALES)[number];

export interface PlanFeature {
  key: string;
  count?: number;
  options?: Record<string, string | number> | string[];
  locales: readonly AvailableLocale[];
}

export interface PlanDescription {
  topList: PlanFeature[];
  bottomListTitleKey: string;
  bottomList: PlanFeature[];
}

export const SUBSCRIPTION_PLAN_DESCRIPTIONS: Record<string, PlanDescription> = {
  lemon: {
    topList: [
      {
        key: "subscription.planDescriptions.limitedResources",
        locales: AVAILABLE_LOCALES,
      },
      {
        key: "subscription.planDescriptions.users",
        count: 1,
        options: { userCount: 1 },
        locales: AVAILABLE_LOCALES,
      },
      {
        key: "subscription.planDescriptions.storage",
        options: { storageVolume: "5 GiB" },
        locales: AVAILABLE_LOCALES,
      },
    ],
    bottomListTitleKey: "subscription.planDescriptions.freeBottomListTitle",
    bottomList: [
      {
        key: "subscription.planDescriptions.publicyAvailableResources",
        locales: AVAILABLE_LOCALES,
      },
      {
        key: "subscription.planDescriptions.basemaps",
        locales: AVAILABLE_LOCALES,
      },
      {
        key: "subscription.planDescriptions.toolbox",
        locales: AVAILABLE_LOCALES,
      },
      {
        key: "subscription.planDescriptions.allSoftware",
        locales: AVAILABLE_LOCALES,
      },
      {
        key: "subscription.planDescriptions.tracking",
        count: 1,
        options: { trackerCount: 1 },
        locales: AVAILABLE_LOCALES,
      },
      {
        key: "subscription.planDescriptions.collector",
        count: 1,
        options: { collectorCount: 1 },
        locales: AVAILABLE_LOCALES,
      },
      {
        key: "subscription.planDescriptions.lookupTables",
        locales: AVAILABLE_LOCALES,
      },
    ],
  },
  mandarin: {
    topList: [
      {
        key: "subscription.planDescriptions.unlimitedResources",
        locales: AVAILABLE_LOCALES,
      },
      {
        key: "subscription.planDescriptions.users",
        count: 1,
        options: { userCount: 1 },
        locales: AVAILABLE_LOCALES,
      },
      {
        key: "subscription.planDescriptions.storage",
        options: { storageVolume: "10 GiB" },
        locales: AVAILABLE_LOCALES,
      },
    ],
    bottomListTitleKey: "subscription.planDescriptions.miniBottomListTitle",
    bottomList: [
      { key: "subscription.planDescriptions.cors", locales: AVAILABLE_LOCALES },
    ],
  },
  pomelo: {
    topList: [
      {
        key: "subscription.planDescriptions.unlimitedResources",
        locales: AVAILABLE_LOCALES,
      },
      {
        key: "subscription.planDescriptions.users",
        count: 5,
        options: { userCount: "5+" },
        locales: AVAILABLE_LOCALES,
      },
      {
        key: "subscription.planDescriptions.storage",
        options: { storageVolume: "50+ GiB" },
        locales: AVAILABLE_LOCALES,
      },
    ],
    bottomListTitleKey: "subscription.planDescriptions.premiumBottomListTitle",
    bottomList: [
      {
        key: "subscription.planDescriptions.privacy",
        locales: AVAILABLE_LOCALES,
      },
      {
        key: "subscription.planDescriptions.toolboxExtended",
        locales: AVAILABLE_LOCALES,
      },
      {
        key: "subscription.planDescriptions.backups",
        locales: AVAILABLE_LOCALES,
      },
      {
        key: "subscription.planDescriptions.tracking",
        count: 5,
        options: { trackerCount: "5+" },
        locales: AVAILABLE_LOCALES,
      },
      {
        key: "subscription.planDescriptions.collector",
        count: 5,
        options: { collectorCount: "5+" },
        locales: AVAILABLE_LOCALES,
      },
      { key: "subscription.planDescriptions.geoservices", locales: ["ru"] },
      { key: "subscription.planDescriptions.cadastre", locales: ["ru"] },
      {
        key: "subscription.planDescriptions.domainName",
        locales: AVAILABLE_LOCALES,
      },
      {
        key: "subscription.planDescriptions.branding",
        locales: AVAILABLE_LOCALES,
      },
      {
        key: "subscription.planDescriptions.speed",
        locales: AVAILABLE_LOCALES,
      },
      {
        key: "subscription.planDescriptions.support",
        locales: AVAILABLE_LOCALES,
      },
    ],
  },
};

export const INDUSTRY_VALUES = [
  "agriculture",
  "forestry",
  "environment",
  "architecture",
  "it",
  "retail",
  "watermanagement",
  "hunt",
  "telecom",
  "realestate",
  "transport",
  "cadastre",
  "utilities",
  "tourism",
  "uav",
  "finances",
  "manufacturing",
  "mining",
  "energy",
  "history",
  "healthcare",
  "emergencies",
  "elections",
  "media",
  "mapping",
  "datascience",
  "other",
] as const;

export const ROLE_VALUES = [
  "student",
  "employee",
  "freelancer",
  "unemployed",
  "self-employed",
  "intern",
  "other",
] as const;

export const SUPPORT_GROUP_VALUES = ["support", "sales", "demo"] as const;
